/**
 * Register Component - Usa Firebase Authentication
 * Registro en 2 pasos: Datos personales → Datos de empresa
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  Collapse,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  Google as GoogleIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from "firebase/auth";

const API_URL = "/api";

// Países y ciudades
const countryOptions = [
  { code: "CO", name: "Colombia" },
  { code: "MX", name: "México" },
  { code: "PA", name: "Panamá" },
];

const citiesByCountry = {
  CO: ["Bogotá", "Medellín", "Cali", "Barranquilla", "Cartagena", "Bucaramanga", "Pereira", "Santa Marta", "Manizales", "Cúcuta"],
  MX: ["Ciudad de México", "Guadalajara", "Monterrey", "Puebla", "Tijuana", "León", "Querétaro", "Cancún", "Mérida", "Toluca"],
  PA: ["Ciudad de Panamá", "Colón", "David", "Santiago", "Chitré", "La Chorrera", "Penonomé", "Aguadulce"],
};

// Validaciones
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (/[a-z]/.test(password)) strength += 25;
  if (/[A-Z]/.test(password)) strength += 25;
  if (/\d/.test(password)) strength += 25;
  return strength;
};

const getPasswordStrengthColor = (strength) => {
  if (strength <= 25) return "#ef4444";
  if (strength <= 50) return "#f59e0b";
  if (strength <= 75) return "#3b82f6";
  return "#10b981";
};

const getPasswordStrengthLabel = (strength) => {
  if (strength <= 25) return "Débil";
  if (strength <= 50) return "Regular";
  if (strength <= 75) return "Buena";
  return "Fuerte";
};

export default function Register() {
  const navigate = useNavigate();

  // Estado del stepper
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Datos personales", "Datos de empresa"];

  // Estados del formulario
  const [form, setForm] = useState({
    name: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
    company: "",
    address: "",
    city: "",
    country: "",
    tax_id: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados de validación
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const emailCheckTimeout = useRef(null);

  // Estados de carga y errores
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Limpiar errores
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Verificar email en tiempo real
  const checkEmailExists = async (email) => {
    setCheckingEmail(true);
    try {
      const res = await fetch(`${API_URL}/users/check_email?email=${encodeURIComponent(email)}`);
      const data = await res.json();
      setEmailExists(data.exists);
    } catch {
      setEmailExists(false);
    }
    setCheckingEmail(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError("");

    if (name === "email") {
      setEmailExists(false);
      if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
      if (validateEmail(value)) {
        emailCheckTimeout.current = setTimeout(() => checkEmailExists(value), 500);
      }
    }

    if (name === "country") {
      setForm((f) => ({ ...f, country: value, city: "" }));
    }
  };

  // Validar paso 1
  const validateStep1 = () => {
    if (!form.name.trim() || !form.lastname.trim()) {
      setError("Por favor ingresa tu nombre y apellido");
      return false;
    }
    if (!form.email.trim() || !validateEmail(form.email)) {
      setError("Por favor ingresa un correo electrónico válido");
      return false;
    }
    if (emailExists) {
      setError("Este correo ya está registrado");
      return false;
    }
    if (getPasswordStrength(form.password) < 100) {
      setError("La contraseña debe tener mínimo 8 caracteres, una mayúscula, una minúscula y un número");
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return false;
    }
    return true;
  };

  // Validar paso 2
  const validateStep2 = () => {
    if (!form.tax_id.trim()) {
      setError("Por favor ingresa el NIT/RFC/RUT de tu empresa");
      return false;
    }
    if (!form.company.trim()) {
      setError("Por favor ingresa el nombre de tu empresa");
      return false;
    }
    if (!form.country) {
      setError("Por favor selecciona un país");
      return false;
    }
    if (!form.city) {
      setError("Por favor selecciona una ciudad");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setError("");
  };

  /**
   * Registrar con Firebase y backend
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) return;

    setLoading(true);
    setError("");

    try {
      // 1. Crear usuario en Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const firebaseUser = userCredential.user;

      // 2. Actualizar perfil en Firebase
      await updateProfile(firebaseUser, {
        displayName: `${form.name} ${form.lastname}`,
      });

      // 3. Obtener token y registrar en backend
      const idToken = await firebaseUser.getIdToken();

      const response = await fetch(`${API_URL}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: idToken,
          company: {
            company: form.company,
            address: form.address,
            city: form.city,
            country: form.country,
            tax_id: form.tax_id,
          },
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        localStorage.setItem("user_id", data.user_id);
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        throw new Error(data.detail || "Error al registrar");
      }
    } catch (err) {
      console.error("Registration error:", err);

      if (err.code === "auth/email-already-in-use") {
        setError("Este correo ya está registrado. Intenta iniciar sesión.");
      } else if (err.code === "auth/weak-password") {
        setError("La contraseña es muy débil");
      } else {
        setError(err.message || "Error al registrar. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Registrar con Google
   */
  const handleGoogleRegister = async () => {
    setLoading(true);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });

      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();

      // Intentar autenticar - si es nuevo pedirá datos de empresa
      const response = await fetch(`${API_URL}/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: idToken }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("user_id", data.user_id);
        navigate("/dashboard");
      } else if (response.status === 422) {
        // Usuario nuevo con Google - necesita datos de empresa
        // Pre-llenar nombre del formulario
        const displayName = result.user.displayName || "";
        const nameParts = displayName.split(" ");

        setForm({
          ...form,
          name: nameParts[0] || "",
          lastname: nameParts.slice(1).join(" ") || "",
          email: result.user.email || "",
          password: "GoogleAuth123!", // Placeholder - no se usa
          confirmPassword: "GoogleAuth123!",
        });

        setActiveStep(1); // Ir directo al paso 2
      } else {
        throw new Error(data.detail || "Error al registrar");
      }
    } catch (err) {
      console.error("Google register error:", err);
      if (err.code !== "auth/popup-closed-by-user") {
        setError(err.message || "Error al registrar con Google");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedCountryCode = countryOptions.find((c) => c.name === form.country)?.code;
  const passwordStrength = getPasswordStrength(form.password);

  // Pantalla de éxito
  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e293b 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in timeout={500}>
          <Paper
            elevation={24}
            sx={{
              p: 5,
              borderRadius: 4,
              textAlign: "center",
              maxWidth: 400,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: "#10b981", mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="#1e293b" gutterBottom>
              ¡Registro exitoso!
            </Typography>
            <Typography color="#64748b" sx={{ mb: 3 }}>
              Tu cuenta ha sido creada. Serás redirigido al dashboard...
            </Typography>
            <CircularProgress size={24} sx={{ color: "#6366f1" }} />
          </Paper>
        </Fade>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e293b 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Fade in timeout={500}>
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 4,
            width: "100%",
            maxWidth: 450,
            background: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
          }}
        >
          {/* Logo y título */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                bgcolor: "#1e3a5f",
                boxShadow: "0 8px 24px rgba(30, 58, 95, 0.3)",
              }}
            >
              <img
                src="/images/logo-nara-png.png"
                alt="Nara"
                style={{ width: 78, height: 78, objectFit: "contain" }}
              />
            </Avatar>
            <Typography variant="h5" fontWeight={700} color="#1e293b">
              Crear cuenta
            </Typography>
            <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
              Comienza a usar Nara gratis
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Mensajes de error */}
          <Collapse in={!!error}>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          </Collapse>

          {/* Paso 1: Datos personales */}
          <Collapse in={activeStep === 0}>
            <form onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
              <Stack spacing={2}>
                {/* Nombre y Apellido */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Nombre"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  />
                  <TextField
                    label="Apellido"
                    name="lastname"
                    value={form.lastname}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  />
                </Box>

                {/* Email */}
                <TextField
                  label="Correo electrónico"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  error={emailExists}
                  helperText={
                    checkingEmail
                      ? "Verificando..."
                      : emailExists
                      ? "Este correo ya está registrado"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* Contraseña */}
                <Box>
                  <TextField
                    label="Contraseña"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  />
                  {/* Indicador de fortaleza */}
                  {form.password && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={passwordStrength}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: "#e2e8f0",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: getPasswordStrengthColor(passwordStrength),
                          },
                        }}
                      />
                      <Typography variant="caption" sx={{ color: getPasswordStrengthColor(passwordStrength) }}>
                        {getPasswordStrengthLabel(passwordStrength)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Confirmar contraseña */}
                <TextField
                  label="Confirmar contraseña"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  error={form.confirmPassword && form.password !== form.confirmPassword}
                  helperText={
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? "Las contraseñas no coinciden"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} size="small">
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* Botón siguiente */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading || checkingEmail || emailExists}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.3,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                  }}
                >
                  Siguiente
                </Button>
              </Stack>
            </form>

            {/* Divider y Google */}
            <Divider sx={{ my: 2.5 }}>
              <Typography variant="caption" color="#94a3b8">o regístrate con</Typography>
            </Divider>

            <Button
              variant="outlined"
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleRegister}
              disabled={loading}
              sx={{
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#334155",
              }}
            >
              Google
            </Button>
          </Collapse>

          {/* Paso 2: Datos de empresa */}
          <Collapse in={activeStep === 1}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {/* NIT/RFC/RUT */}
                <TextField
                  label="NIT / RFC / RUT"
                  name="tax_id"
                  value={form.tax_id}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BadgeIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* Nombre empresa */}
                <TextField
                  label="Nombre de la empresa"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* Dirección */}
                <TextField
                  label="Dirección (opcional)"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      background: "#f8fafc",
                    },
                  }}
                />

                {/* País y Ciudad */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    select
                    label="País"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading}
                    SelectProps={{ native: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  >
                    <option value="">Seleccionar</option>
                    {countryOptions.map((c) => (
                      <option key={c.code} value={c.name}>{c.name}</option>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Ciudad"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    disabled={loading || !form.country}
                    SelectProps={{ native: true }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        background: "#f8fafc",
                      },
                    }}
                  >
                    <option value="">Seleccionar</option>
                    {(citiesByCountry[selectedCountryCode] || []).map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </TextField>
                </Box>

                {/* Botones */}
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleBack}
                    disabled={loading}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      py: 1.2,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: "none",
                      borderColor: "#e2e8f0",
                      color: "#64748b",
                    }}
                  >
                    Atrás
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.2,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: "none",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Crear cuenta"}
                  </Button>
                </Box>
              </Stack>
            </form>
          </Collapse>

          {/* Link a login */}
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="body2" color="#64748b">
              ¿Ya tienes cuenta?{" "}
              <Typography
                component="span"
                sx={{
                  color: "#6366f1",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/login")}
              >
                Inicia sesión
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
}
